//CRN_20260402 - ⚠️ ngOnInit() called for components/directives & not services ⚠️

import { ConfigSystemParams } from 'src/app/core/consts';
import { ConfigSystemService } from '../system/config-system/config-system-service';
import { CountryCode, parsePhoneNumberFromString, PhoneNumber, validatePhoneNumberLength } from 'libphonenumber-js';
import { ExceptionsService } from './exceptions-service';
import { Injectable } from '@angular/core';

export interface PhoneNumberValidationResult {
  e164            : string | undefined;
  errorMessage    : string | undefined;
  isValid         : boolean;
  nationalNumber  : string | undefined;
  strippedDialCode: boolean;
}

@Injectable({ providedIn: 'root' })

export class PhoneNumberService {

  get digitsOnly () { return this.configSystemService.getValueAsBoolean(ConfigSystemParams.PhoneNumberServiceDigitsOnly, true); }

  private readonly e164MaximumDigits: number=15;
  private readonly e164MinimumDigits: number=7;

  constructor(
    private configSystemService: ConfigSystemService,
    private exceptionsService  : ExceptionsService
  ) {

  }

  private formatOutput(value: string): string {
    try {
      return this.digitsOnly ? value.replace(/\D+/g, '') : value;
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return value;
    }
  }

  private isDialCodeRepeated(value: string, code: string): boolean {
    try {
      return !!code && value.startsWith(code+code);
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return false;
    }
  }

  private isFullyValid(subscriberNumber: string, diallingCode: string, alpha2Code?: string | null): boolean {
    try {
      const country: CountryCode | undefined=this.toCountryCode(alpha2Code);

      if (!country)
        return this.isPlausibleSubscriberNumber(subscriberNumber, diallingCode, alpha2Code);

      const parsed: PhoneNumber | undefined=parsePhoneNumberFromString("+"+diallingCode+subscriberNumber);

      return !!parsed && parsed.isValid() && parsed.country==country;
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return false;
    }
  }

  private isPlausibleSubscriberNumber(subscriberNumber: string, diallingCode: string, alpha2Code?: string | null): boolean {
    try {
      const digits: string=this.toDigits(subscriberNumber);

      if (!digits)
        return false;

      const country: CountryCode | undefined=this.toCountryCode(alpha2Code);

      if (country)
        return validatePhoneNumberLength(digits, country)==undefined;

      const total: number=this.toDigits(diallingCode).length+digits.length;

      return total >= this.e164MinimumDigits && total <= this.e164MaximumDigits;
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return false;
    }
  }

  public stripDuplicatedDialCode(subscriberNumber: string,
                                 diallingCode    : string,
                                 alpha2Code?     : string | null
                                ): { strippedDialCode: boolean; value: string } {
    try {
      let   value: string=this.toDigits(subscriberNumber);
      const code : string=this.toDigits(diallingCode ?? "");

      if (!value || !code || !value.startsWith(code))
        return { strippedDialCode: false, value: value };

      const remainder: string=this.stripLeadingZeros(value.slice(code.length));

      if (!remainder)
        return { strippedDialCode: false, value: value };

      if (!this.isPlausibleSubscriberNumber(remainder, code, alpha2Code))
        return { strippedDialCode: false, value: value };

      const validAsTyped : boolean=this.isFullyValid(value,     code, alpha2Code);
      const validStripped: boolean=this.isFullyValid(remainder, code, alpha2Code);

      if (validAsTyped && !this.isDialCodeRepeated(value, code))
        return { strippedDialCode: false, value: value };

      if (!validStripped)
        return { strippedDialCode: false, value: value };

      return { strippedDialCode: true, value: remainder };
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return { strippedDialCode: false, value: subscriberNumber };
    }
  }

  private toCountryCode(alpha2Code?: string | null): CountryCode | undefined {
    try {
      const code: string=(alpha2Code ?? "").trim().toUpperCase();

      if (code.length != 2)
        return undefined;

      if (validatePhoneNumberLength("1", <CountryCode>code)=="INVALID_COUNTRY")
        return undefined;

      return <CountryCode>code;
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return undefined;
    }
  }

  public toDigits(value: null | string | undefined): string {
    try {
      return (value ?? "").replace(/\D+/g, '');
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return "";
    }
  }

  public stripLeadingZeros(value: null | string | undefined): string {
    try {
      return (value ?? "").replace(/^0+/, "");
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return value ?? "";
    }
  }

  public validateAgainstDialCode(subscriberNumber: null | string | undefined,
                                 diallingCode    : null | string | undefined,
                                 alpha2Code?     : null | string
                                ): PhoneNumberValidationResult {
    try {
      const failed=(errorMessage: string, strippedDialCode: boolean=false): PhoneNumberValidationResult => ({
        e164            : undefined,
        errorMessage    : errorMessage,
        isValid         : false,
        nationalNumber  : undefined,
        strippedDialCode: strippedDialCode
      });

      const code: string=this.toDigits(diallingCode);

      if (!code)
        return failed("Select a country code (on left)");

      let digits: string=this.stripLeadingZeros(this.toDigits(subscriberNumber));

      if (!digits)
        return failed("A value is required for mobile");

      const stripped=this.stripDuplicatedDialCode(digits, code, alpha2Code);

      digits=stripped.value;

      if (!digits)
        return failed("A value is required for mobile", stripped.strippedDialCode);

      const country: CountryCode | undefined=this.toCountryCode(alpha2Code);
      const parsed : PhoneNumber | undefined=parsePhoneNumberFromString("+"+code+digits);

      if (parsed && country && parsed.country==country) {
        if (!parsed.isValid())
          return failed("Enter a valid mobile number for the selected country", stripped.strippedDialCode);

        return {
          e164            : "+"+code+parsed.nationalNumber,
          errorMessage    : undefined,
          isValid         : true,
          nationalNumber  : <string>parsed.nationalNumber,
          strippedDialCode: stripped.strippedDialCode
        };
      }

      if (!this.isPlausibleSubscriberNumber(digits, code, country ? undefined : alpha2Code))
        return failed("Enter a valid mobile number for the selected country", stripped.strippedDialCode);

      return {
        e164            : "+"+code+digits,
        errorMessage    : undefined,
        isValid         : true,
        nationalNumber  : digits,
        strippedDialCode: stripped.strippedDialCode
      };
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return {
        e164            : undefined,
        errorMessage    : "Unable to validate the mobile number",
        isValid         : false,
        nationalNumber  : undefined,
        strippedDialCode: false
      };
    }
  }

  public localisePhoneNumber(phoneNumber: string): string {
    try {
      if (!phoneNumber)
        return "";

      //CRN_20260402 - Remove spaces, brackets & dashes
      let cleaned: string=phoneNumber.replace(/[\s\-\(\)]+/g, '');

      //CRN_20260402 - Convert 00 prefix to +
      if (cleaned.startsWith('00'))
        cleaned='+' + cleaned.slice(2);

      //CRN_20260402 - Already local
      if (cleaned.startsWith('0'))
        return this.formatOutput(cleaned);

      //CRN_20260402 - Only parse numbers with +
      if (!cleaned.startsWith('+'))
        return this.formatOutput(cleaned);

      //CRN_20260402 - Parse & format
      const parsed: PhoneNumber | undefined=parsePhoneNumberFromString(cleaned);

      if (!parsed)
        return this.formatOutput(cleaned);

      if (parsed.isValid())
        return this.formatOutput(parsed.formatNational());

      return this.formatOutput(cleaned);
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return "";
    }
  }

  // public localisePhoneNumber(phoneNumber: string): string {
  //   try {
  //     if (!phoneNumber)
  //       return "";

  //     //CRN_20260404 - Remove spaces
  //     let cleaned: string=phoneNumber.replace(/\s+/g, '');

  //     //CRN_20260404 - Already local
  //     if (cleaned.startsWith('0'))
  //       return cleaned;
      
  //     if (!cleaned.startsWith('+'))
  //       cleaned='+' + cleaned;

  //     const countryCodeMap: { code: string; trunk: string }[]=[

  //       //CRN_20260402 - 4-digit

  //       { code: '+1242', trunk: '0' }, //Bahamas
  //       { code: '+1246', trunk: '0' }, //Barbados
  //       { code: '+1264', trunk: '0' }, //Anguilla
  //       { code: '+1268', trunk: '0' }, //Antigua
  //       { code: '+1284', trunk: '0' }, //BVI
  //       { code: '+1340', trunk: '0' }, //USVI
  //       { code: '+1345', trunk: '0' }, //Cayman Islands
  //       { code: '+1441', trunk: '0' }, //Bermuda
  //       { code: '+1473', trunk: '0' }, //Grenada
  //       { code: '+1649', trunk: '0' }, //Turks & Caicos
  //       { code: '+1664', trunk: '0' }, //Montserrat
  //       { code: '+1670', trunk: '0' }, //CNMI
  //       { code: '+1671', trunk: '0' }, //Guam
  //       { code: '+1684', trunk: '0' }, //American Samoa
  //       { code: '+1721', trunk: '0' }, //Sint Maarten
  //       { code: '+1758', trunk: '0' }, //St Lucia
  //       { code: '+1767', trunk: '0' }, //Dominica
  //       { code: '+1784', trunk: '0' }, //St Vincent
  //       { code: '+1787', trunk: '0' }, //Puerto Rico
  //       { code: '+1809', trunk: '0' }, //Dominican Republic
  //       { code: '+1829', trunk: '0' }, //Dominican Republic
  //       { code: '+1849', trunk: '0' }, //Dominican Republic
  //       { code: '+1868', trunk: '0' }, //Trinidad & Tobago
  //       { code: '+1869', trunk: '0' }, //St Kitts
  //       { code: '+1876', trunk: '0' }, //Jamaica
  //       { code: '+1939', trunk: '0' }, //Puerto Rico

  //       //CRN_20260402 - 3-digit

  //       { code: '+212', trunk: '0' },  //Morocco
  //       { code: '+213', trunk: '0' },  //Algeria
  //       { code: '+216', trunk: '0' },  //Tunisia
  //       { code: '+218', trunk: '0' },  //Libya
  //       { code: '+220', trunk: '0' },  //Gambia
  //       { code: '+221', trunk: '0' },  //Senegal
  //       { code: '+222', trunk: '0' },  //Mauritania
  //       { code: '+223', trunk: '0' },  //Mali
  //       { code: '+224', trunk: '0' },  //Guinea
  //       { code: '+225', trunk: '0' },  //Ivory Coast
  //       { code: '+226', trunk: '0' },  //Burkina Faso
  //       { code: '+227', trunk: '0' },  //Niger
  //       { code: '+228', trunk: '0' },  //Togo
  //       { code: '+229', trunk: '0' },  //Benin
  //       { code: '+230', trunk: '' },   //Mauritius (no trunk)
  //       { code: '+231', trunk: '0' },  //Liberia
  //       { code: '+232', trunk: '0' },  //Sierra Leone
  //       { code: '+233', trunk: '0' },  //Ghana
  //       { code: '+234', trunk: '0' },  //Nigeria
  //       { code: '+235', trunk: '0' },  //Chad
  //       { code: '+236', trunk: '0' },  //CAR
  //       { code: '+237', trunk: '0' },  //Cameroon
  //       { code: '+238', trunk: '0' },  //Cape Verde
  //       { code: '+239', trunk: '0' },  //Sao Tome
  //       { code: '+240', trunk: '0' },  //Equatorial Guinea
  //       { code: '+241', trunk: '0' },  //Gabon
  //       { code: '+242', trunk: '0' },  //Congo
  //       { code: '+243', trunk: '0' },  //DRC
  //       { code: '+244', trunk: '0' },  //Angola
  //       { code: '+245', trunk: '0' },  //Guinea-Bissau
  //       { code: '+246', trunk: '0' },  //British Indian Ocean Territory
  //       { code: '+247', trunk: '0' },  //Ascension Island
  //       { code: '+248', trunk: '0' },  //Seychelles
  //       { code: '+249', trunk: '0' },  //Sudan
  //       { code: '+250', trunk: '0' },  //Rwanda
  //       { code: '+251', trunk: '0' },  //Ethiopia
  //       { code: '+252', trunk: '0' },  //Somalia
  //       { code: '+253', trunk: '0' },  //Djibouti
  //       { code: '+254', trunk: '0' },  //Kenya
  //       { code: '+255', trunk: '0' },  //Tanzania
  //       { code: '+256', trunk: '0' },  //Uganda
  //       { code: '+257', trunk: '0' },  //Burundi
  //       { code: '+258', trunk: '0' },  //Mozambique
  //       { code: '+260', trunk: '0' },  //Zambia
  //       { code: '+261', trunk: '0' },  //Madagascar
  //       { code: '+262', trunk: '0' },  //Reunion
  //       { code: '+263', trunk: '0' },  //Zimbabwe
  //       { code: '+264', trunk: '0' },  //Namibia
  //       { code: '+265', trunk: '0' },  //Malawi
  //       { code: '+266', trunk: '0' },  //Lesotho
  //       { code: '+267', trunk: '0' },  //Botswana
  //       { code: '+268', trunk: '0' },  //Eswatini
  //       { code: '+269', trunk: '0' },  //Comoros
  //       { code: '+290', trunk: '0' },  //St Helena
  //       { code: '+291', trunk: '0' },  //Eritrea
  //       { code: '+297', trunk: '0' },  //Aruba
  //       { code: '+298', trunk: '0' },  //Faroe Islands
  //       { code: '+299', trunk: '0' },  //Greenland
  //       { code: '+350', trunk: '0' },  //Gibraltar
  //       { code: '+351', trunk: '0' },  //Portugal
  //       { code: '+352', trunk: '' },   //Luxembourg (no trunk)
  //       { code: '+353', trunk: '0' },  //Ireland
  //       { code: '+354', trunk: '' },   //Iceland (no trunk)
  //       { code: '+355', trunk: '0' },  //Albania
  //       { code: '+356', trunk: '' },   //Malta (no trunk)
  //       { code: '+357', trunk: '' },   //Cyprus (no trunk)
  //       { code: '+358', trunk: '0' },  //Finland
  //       { code: '+359', trunk: '0' },  //Bulgaria
  //       { code: '+370', trunk: '0' },  //Lithuania
  //       { code: '+371', trunk: '' },   //Latvia (no trunk)
  //       { code: '+372', trunk: '' },   //Estonia (no trunk)
  //       { code: '+373', trunk: '0' },  //Moldova
  //       { code: '+374', trunk: '0' },  //Armenia
  //       { code: '+375', trunk: '0' },  //Belarus
  //       { code: '+376', trunk: '' },   //Andorra (no trunk)
  //       { code: '+377', trunk: '' },   //Monaco (no trunk)
  //       { code: '+378', trunk: '' },   //San Marino (no trunk)
  //       { code: '+380', trunk: '0' },  //Ukraine
  //       { code: '+381', trunk: '0' },  //Serbia
  //       { code: '+382', trunk: '0' },  //Montenegro
  //       { code: '+385', trunk: '0' },  //Croatia
  //       { code: '+386', trunk: '0' },  //Slovenia
  //       { code: '+387', trunk: '0' },  //Bosnia
  //       { code: '+389', trunk: '0' },  //North Macedonia
  //       { code: '+420', trunk: '' },   //Czech Republic (no trunk)
  //       { code: '+421', trunk: '0' },  //Slovakia
  //       { code: '+423', trunk: '' },   //Liechtenstein (no trunk)
  //       { code: '+500', trunk: '' },   //Falkland Islands (no trunk)
  //       { code: '+501', trunk: '0' },  //Belize
  //       { code: '+502', trunk: '' },   //Guatemala (no trunk)
  //       { code: '+503', trunk: '' },   //El Salvador (no trunk)
  //       { code: '+504', trunk: '' },   //Honduras (no trunk)
  //       { code: '+505', trunk: '' },   //Nicaragua (no trunk)
  //       { code: '+506', trunk: '' },   //Costa Rica (no trunk)
  //       { code: '+507', trunk: '' },   //Panama (no trunk)
  //       { code: '+508', trunk: '0' },  //St Pierre & Miquelon
  //       { code: '+509', trunk: '' },   //Haiti (no trunk)
  //       { code: '+590', trunk: '0' },  //Guadeloupe
  //       { code: '+591', trunk: '0' },  //Bolivia
  //       { code: '+592', trunk: '' },   //Guyana (no trunk)
  //       { code: '+593', trunk: '0' },  //Ecuador
  //       { code: '+594', trunk: '0' },  //French Guiana
  //       { code: '+595', trunk: '0' },  //Paraguay
  //       { code: '+596', trunk: '0' },  //Martinique
  //       { code: '+597', trunk: '' },   //Suriname (no trunk)
  //       { code: '+598', trunk: '0' },  //Uruguay
  //       { code: '+599', trunk: '0' },  //Netherlands Antilles
  //       { code: '+670', trunk: '' },   //East Timor (no trunk)
  //       { code: '+672', trunk: '0' },  //Norfolk Island
  //       { code: '+673', trunk: '' },   //Brunei (no trunk)
  //       { code: '+674', trunk: '' },   //Nauru (no trunk)
  //       { code: '+675', trunk: '' },   //Papua New Guinea (no trunk)
  //       { code: '+676', trunk: '' },   //Tonga (no trunk)
  //       { code: '+677', trunk: '' },   //Solomon Islands (no trunk)
  //       { code: '+678', trunk: '' },   //Vanuatu (no trunk)
  //       { code: '+679', trunk: '' },   //Fiji (no trunk)
  //       { code: '+680', trunk: '' },   //Palau (no trunk)
  //       { code: '+681', trunk: '' },   //Wallis & Futuna (no trunk)
  //       { code: '+682', trunk: '' },   //Cook Islands (no trunk)
  //       { code: '+683', trunk: '' },   //Niue (no trunk)
  //       { code: '+685', trunk: '' },   //Samoa (no trunk)
  //       { code: '+686', trunk: '' },   //Kiribati (no trunk)
  //       { code: '+687', trunk: '' },   //New Caledonia (no trunk)
  //       { code: '+688', trunk: '' },   //Tuvalu (no trunk)
  //       { code: '+689', trunk: '' },   //French Polynesia (no trunk)
  //       { code: '+690', trunk: '' },   //Tokelau (no trunk)
  //       { code: '+691', trunk: '' },   //Micronesia (no trunk)
  //       { code: '+692', trunk: '' },   //Marshall Islands (no trunk)
  //       { code: '+850', trunk: '' },   //North Korea (no trunk)
  //       { code: '+852', trunk: '' },   //Hong Kong (no trunk)
  //       { code: '+853', trunk: '' },   //Macau (no trunk)
  //       { code: '+855', trunk: '0' },  //Cambodia
  //       { code: '+856', trunk: '0' },  //Laos
  //       { code: '+880', trunk: '0' },  //Bangladesh
  //       { code: '+886', trunk: '0' },  //Taiwan
  //       { code: '+960', trunk: '' },   //Maldives (no trunk)
  //       { code: '+961', trunk: '0' },  //Lebanon
  //       { code: '+962', trunk: '0' },  //Jordan
  //       { code: '+963', trunk: '0' },  //Syria
  //       { code: '+964', trunk: '0' },  //Iraq
  //       { code: '+965', trunk: '' },   //Kuwait (no trunk)
  //       { code: '+966', trunk: '0' },  //Saudi Arabia
  //       { code: '+967', trunk: '0' },  //Yemen
  //       { code: '+968', trunk: '' },   //Oman (no trunk)
  //       { code: '+970', trunk: '0' },  //Palestine
  //       { code: '+971', trunk: '0' },  //UAE
  //       { code: '+972', trunk: '0' },  //Israel
  //       { code: '+973', trunk: '' },   //Bahrain (no trunk)
  //       { code: '+974', trunk: '' },   //Qatar (no trunk)
  //       { code: '+975', trunk: '0' },  //Bhutan
  //       { code: '+976', trunk: '0' },  //Mongolia
  //       { code: '+977', trunk: '0' },  //Nepal
  //       { code: '+992', trunk: '' },   //Tajikistan (no trunk)
  //       { code: '+993', trunk: '0' },  //Turkmenistan
  //       { code: '+994', trunk: '0' },  //Azerbaijan
  //       { code: '+995', trunk: '0' },  //Georgia
  //       { code: '+996', trunk: '0' },  //Kyrgyzstan
  //       { code: '+998', trunk: '0' },  //Uzbekistan

  //       //CRN_20260402 - 2-digit codes

  //       { code: '+20',  trunk: '0' },  //Egypt
  //       { code: '+27',  trunk: '0' },  //South Africa
  //       { code: '+30',  trunk: '0' },  //Greece
  //       { code: '+31',  trunk: '0' },  //Netherlands
  //       { code: '+32',  trunk: '0' },  //Belgium
  //       { code: '+33',  trunk: '0' },  //France
  //       { code: '+34',  trunk: '' },   //Spain (no trunk)
  //       { code: '+36',  trunk: '06' }, //Hungary
  //       { code: '+39',  trunk: '0' },  //Italy
  //       { code: '+40',  trunk: '0' },  //Romania
  //       { code: '+41',  trunk: '0' },  //Switzerland
  //       { code: '+43',  trunk: '0' },  //Austria
  //       { code: '+44',  trunk: '0' },  //UK
  //       { code: '+45',  trunk: '' },   //Denmark (no trunk)
  //       { code: '+46',  trunk: '0' },  //Sweden
  //       { code: '+47',  trunk: '' },   //Norway (no trunk)
  //       { code: '+48',  trunk: '0' },  //Poland
  //       { code: '+49',  trunk: '0' },  //Germany
  //       { code: '+51',  trunk: '0' },  //Peru
  //       { code: '+52',  trunk: '0' },  //Mexico
  //       { code: '+53',  trunk: '0' },  //Cuba
  //       { code: '+54',  trunk: '0' },  //Argentina
  //       { code: '+55',  trunk: '0' },  //Brazil
  //       { code: '+56',  trunk: '0' },  //Chile
  //       { code: '+57',  trunk: '0' },  //Colombia
  //       { code: '+58',  trunk: '0' },  //Venezuela
  //       { code: '+60',  trunk: '0' },  //Malaysia
  //       { code: '+61',  trunk: '0' },  //Australia
  //       { code: '+62',  trunk: '0' },  //Indonesia
  //       { code: '+63',  trunk: '0' },  //Philippines
  //       { code: '+64',  trunk: '0' },  //New Zealand
  //       { code: '+65',  trunk: '' },   //Singapore (no trunk)
  //       { code: '+66',  trunk: '0' },  //Thailand
  //       { code: '+81',  trunk: '0' },  //Japan
  //       { code: '+82',  trunk: '0' },  //South Korea
  //       { code: '+84',  trunk: '0' },  //Vietnam
  //       { code: '+86',  trunk: '0' },  //China
  //       { code: '+90',  trunk: '0' },  //Turkey
  //       { code: '+91',  trunk: '0' },  //India
  //       { code: '+92',  trunk: '0' },  //Pakistan
  //       { code: '+93',  trunk: '0' },  //Afghanistan
  //       { code: '+94',  trunk: '0' },  //Sri Lanka
  //       { code: '+95',  trunk: '0' },  //Myanmar
  //       { code: '+98',  trunk: '0' },  //Iran

  //       //CRN_20260402 - 1-digit

  //       { code: '+1',   trunk: '0' },  //USA / Canada
  //       { code: '+7',   trunk: '8' },  //Russia / Kazakhstan
  //     ];

  //     for (const entry of countryCodeMap) {
  //       if (cleaned.startsWith(entry.code)) {
  //         const national: string=cleaned.slice(entry.code.length);

  //         return entry.trunk+national;
  //       }
  //     }

  //     //CRN_20260402 - Unrecognised
  //     return cleaned;
  //   }
  //   catch(e) {
  //     this.exceptionsService.handleException(<Error>e);
  //     return "";
  //   }
  // }
}
